/**
 * Planning and running a merge.
 *
 * Impact and plan are deliberately separate calls. The wizard re-measures as
 * records are ticked and unticked, and doing that through the plan endpoint
 * would leave a trail of abandoned draft rows behind every operator who
 * changed their mind.
 *
 * The plan poll is registered as CONTEXT and never persisted: a merge's status
 * restored from IndexedDB would paint "running" for a job that finished hours
 * ago, and the one thing an operator must be able to trust here is whether the
 * work is still moving.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'

export interface MergeBlocker {
  reason: string
  detail: string
  partnership_uuid: string
}

export interface MergeConflictOption {
  value: string
  record_count: number
  is_survivor: boolean
}

/** A field the records disagree on. The operator picks one value per conflict. */
export interface MergeConflict {
  code_name: string
  display_name: string
  options: MergeConflictOption[]
}

export interface MergeImpact {
  registrations_affected: number
  people_affected: number
  records_merged: number
  locations_moved: string[]
  conflicts: MergeConflict[]
}

export interface MergeItem {
  uuid: string
  partnership_uuid: string
  partner_name: string
  status: string
  registrations_affected: number
  result: Record<string, unknown>
  error: string
  finished_at: string | null
}

export interface MergePlan {
  uuid: string
  survivor_uuid: string
  survivor_name: string
  status: string
  registrations_affected: number
  people_affected: number
  blockers: MergeBlocker[]
  can_proceed: boolean
  items: MergeItem[]
  started_at: string | null
  finished_at: string | null
  error: string
  created_at: string
}

/** Statuses after which nothing more will happen on its own. */
export const TERMINAL_MERGE_STATUSES = [
  'completed',
  'partial',
  'failed',
  'cancelled',
]

export function useMergeImpact(
  survivorUuid: string | undefined,
  loserUuids: string[],
) {
  const { orgUuid } = useOrg()
  return useQuery<MergeImpact>({
    queryKey: ['merge-impact', orgUuid, survivorUuid, [...loserUuids].sort()],
    queryFn: () =>
      api
        .post(
          `/organisations/${orgUuid}/partnerships/${survivorUuid}/merge-impact/`,
          { loser_partnership_uuids: loserUuids },
        )
        .then((r) => r.data),
    enabled: !!orgUuid && !!survivorUuid && loserUuids.length > 0,
  })
}

export interface CreateMergePlanInput {
  loserUuids: string[]
  /** {code_name: chosen value} where the records disagree. */
  fieldChoices?: Record<string, string>
}

export function useCreateMergePlan(survivorUuid: string | undefined) {
  const { orgUuid } = useOrg()
  return useMutation<MergePlan, unknown, CreateMergePlanInput>({
    mutationFn: ({ loserUuids, fieldChoices }) =>
      api
        .post(
          `/organisations/${orgUuid}/partnerships/${survivorUuid}/merge-plans/`,
          {
            loser_partnership_uuids: loserUuids,
            field_choices: fieldChoices ?? {},
          },
        )
        .then((r) => r.data),
  })
}

export function useStartMergePlan() {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation<MergePlan, unknown, string>({
    mutationFn: (planUuid) =>
      api
        .post(`/organisations/${orgUuid}/merge-plans/${planUuid}/start/`)
        .then((r) => r.data),
    onSuccess: () => {
      // Records have been retired and registrations re-attributed, so every
      // list of partners is now wrong until it refetches.
      queryClient.invalidateQueries({ queryKey: ['partnerships'] })
      queryClient.invalidateQueries({ queryKey: ['partnership-detail'] })
    },
  })
}

/**
 * Poll a plan while it is still moving.
 *
 * Stops on a terminal status rather than polling forever - under the immediate
 * backend the merge is already finished by the time the start call returns, and
 * a fixed interval would keep hitting the API for nothing.
 */
export function useMergePlan(planUuid: string | undefined) {
  const { orgUuid } = useOrg()
  return useQuery<MergePlan>({
    queryKey: ['merge-plan', orgUuid, planUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/merge-plans/${planUuid}/`)
        .then((r) => r.data),
    enabled: !!orgUuid && !!planUuid,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status && TERMINAL_MERGE_STATUSES.includes(status) ? false : 1500
    },
  })
}


/** One record inside a duplicate group, as the health scan saw it. */
/** Where one record's customers actually are, busiest place first. */
export interface DuplicateMemberLocation {
  place: string
  count: number
}

export interface DuplicateGroupMember {
  partnership_uuid: string
  name?: string
  account_number?: string
  registration_count?: number
  top_locations?: DuplicateMemberLocation[]
}

export interface DuplicateGroup {
  key: string
  display_name: string
  bucket: string
  record_count: number
  registration_count: number
  members: DuplicateGroupMember[]
  suggested_survivor_uuid?: string | null
  /** Why the scan bucketed the group this way, e.g. 'parent_conflict'. */
  flags?: string[]
  account_numbers?: string[]
  email_domains?: string[]
}

/**
 * The duplicate group this record belongs to, if any.
 *
 * Registered as REFERENCE-shaped data on purpose: it comes from a periodic scan,
 * not from live state, so refetching it on every mount buys nothing. Records
 * merged away since the scan ran are dropped server-side, so a group here is
 * one an operator can still act on.
 */
export function usePartnerDuplicates(partnershipUuid: string | undefined) {
  const { orgUuid } = useOrg()
  return useQuery<DuplicateGroup | null>({
    queryKey: ['partnership-duplicates', orgUuid, partnershipUuid],
    queryFn: () =>
      api
        .get(
          `/organisations/${orgUuid}/partnership-health/duplicates/?partnership=${partnershipUuid}`,
        )
        .then((r) => (r.data?.result ?? r.data)?.groups?.[0] ?? null),
    enabled: !!orgUuid && !!partnershipUuid,
  })
}


/**
 * Record that a duplicate group is not one business.
 *
 * Invalidates the duplicate queries so the group disappears immediately rather
 * than lingering until the next scan - the operator has just made a decision
 * and should see it take effect.
 */
export function useDismissDuplicateGroup() {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, string>({
    mutationFn: (groupKey) =>
      api
        .post(
          `/organisations/${orgUuid}/partnership-health/duplicates/dismiss/`,
          { group_key: groupKey },
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partnership-duplicates'] })
      queryClient.invalidateQueries({
        queryKey: ['partnership-health-duplicates'],
      })
      queryClient.invalidateQueries({ queryKey: ['partnership-health'] })
    },
  })
}
